import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import logger from "@/lib/logger";
import { trackEvent, identifyUser, resetAnalytics } from "@/lib/analytics";
import { refreshSession, resetAuthClearedFlag, api } from "@/lib/api";
const syncTimezone = (detected) => api.syncTimezone(detected);

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);
  const [bootUnreachable, setBootUnreachable] = useState(false);

  // Check existing session on mount. Behaviour by response type:
  //   * 200 → set user from body.
  //   * 401 → try ONE silent refresh (via shared refreshSession helper so it
  //     dedupes with the interceptor path). On refresh success, refetch /me.
  //     On refresh failure, set user=false.
  //   * Any non-401 error (network drop, 5xx, timeout) → do NOT log the user
  //     out. Retry /auth/me ONCE after a short backoff. If that also fails
  //     with a non-401, leave the session undetermined and surface a
  //     "can't reach the server" state via bootUnreachable — the app shell
  //     keeps whatever session state was already there instead of flashing
  //     the login screen for a transient blip.
  useEffect(() => {
    let cancelled = false;
    const tryMe = () => axios.get(`${API}/api/auth/me`, { withCredentials: true });
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    const settle = (u) => {
      // Terminal helper — only call once per boot. Sets user then clears
      // loading. NEVER call this from the unreachable branch: loading must
      // stay true so ProtectedRoute renders PageSkeleton instead of
      // redirecting to /login.
      if (cancelled) return;
      setUser(u);
      setLoading(false);
    };

    (async () => {
      try {
        const res = await tryMe();
        settle(res.data);
        return;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          try {
            await refreshSession();
            const retry = await tryMe();
            settle(retry.data);
          } catch {
            settle(false);
          }
          return;
        }
        // Non-401 (network / 5xx / timeout) — DO NOT clear the session.
        await wait(1500);
        if (cancelled) return;
        try {
          const retry = await tryMe();
          settle(retry.data);
          return;
        } catch (retryErr) {
          const retryStatus = retryErr?.response?.status;
          if (retryStatus === 401) {
            try {
              await refreshSession();
              const retry2 = await tryMe();
              settle(retry2.data);
            } catch {
              settle(false);
            }
            return;
          }
          // Genuinely unreachable. Leave user=null AND loading=true so
          // ProtectedRoute's `if (loading) return <PageSkeleton/>` fires
          // and the app does NOT flash the login screen for a transient
          // outage. bootUnreachable lets higher layers surface a banner.
          if (!cancelled) setBootUnreachable(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Listen for the api.js interceptor telling us a refresh definitively
  // failed. Clear user state exactly once — the setter is idempotent, so
  // repeated events are a no-op.
  useEffect(() => {
    const onAuthCleared = () => {
      setUser((prev) => (prev === false ? prev : false));
    };
    window.addEventListener("mmntm:auth-cleared", onAuthCleared);
    return () => window.removeEventListener("mmntm:auth-cleared", onAuthCleared);
  }, []);

  // U06b: on boot, align the stored timezone with the browser's once per session.
  // The profile endpoint itself honours timezone_auto_detect (default true) and
  // only writes when the zone is valid and different. Silent — no toast.
  useEffect(() => {
    if (!user || typeof window === "undefined") return;
    const key = `mmntm:tz-synced:${user.id || user._id || ""}`;
    try {
      if (sessionStorage.getItem(key)) return;
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!detected) return;
      sessionStorage.setItem(key, "1");
      syncTimezone(detected).catch((e) => logger.warn("timezone sync skipped", e?.message));
    } catch (e) {
      logger.warn("timezone sync unavailable", e?.message);
    }
  }, [user]);


  // Identify the logged-in user to Crisp (loaded via index.html). Safe no-op if
  // Crisp hasn't finished loading yet — it's a queue, calls replay on init.
  // Skip if user is null (loading) or false (logged out).
  useEffect(() => {
    if (!user || typeof window === "undefined" || !window.$crisp) return;
    try {
      if (user.email) window.$crisp.push(["set", "user:email", [user.email]]);
      if (user.name) window.$crisp.push(["set", "user:nickname", [user.name]]);
      if (user.id) window.$crisp.push(["set", "session:data", [[["user_id", user.id]]]]);
    } catch (err) {
      // Crisp loader not ready yet — harmless, the next user state change retries.
      // Logged at debug-only level so we have a trail if Crisp wiring breaks.
      logger.debug("crisp:identify deferred", err);
    }
  }, [user]);

  // Reset Crisp session on logout so the next user doesn't inherit context
  useEffect(() => {
    if (user === false && typeof window !== "undefined" && window.$crisp) {
      try { window.$crisp.push(["do", "session:reset"]); }
      catch (err) { logger.debug("crisp:reset deferred", err); }
    }
  }, [user]);

  const finishLogin = useCallback((data) => {
    setUser(data);
    resetAuthClearedFlag();
    // Bundle L PR-1: clear per-session UI dismissal flags on fresh login
    // so previously-deferred reward modals reappear for the new session.
    try { localStorage.removeItem("mmntm:level-up-dismissed-session"); } catch { /* ignore */ }
    identifyUser(data.id, { tier: data.tier });
    trackEvent("user_logged_in");
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      // Bundle S-1: 2FA accounts get a challenge instead of a session.
      if (data?.status === "2fa_required") return { success: true, twoFactorRequired: true, challenge: data.challenge };
      finishLogin(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  }, [finishLogin]);

  const verifyTwoFactor = useCallback(async (challenge, code) => {
    try {
      const { data } = await axios.post(
        `${API}/api/auth/2fa/verify`,
        { challenge, code },
        { withCredentials: true }
      );
      finishLogin(data);
      return { success: true };
    } catch (e) {
      return { success: false, status: e.response?.status, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  }, [finishLogin]);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(data);
      return data;
    } catch (e) {
      logger.error("refreshUser:", e);
      return null;
    }
  }, []);

  const register = useCallback(async (email, password, name, referral_code = "", confirmEmail = false) => {
    try {
      const { data } = await axios.post(
        `${API}/api/auth/register`,
        { email, password, name, referral_code, ...(confirmEmail ? { confirm_email: true } : {}) },
        { withCredentials: true }
      );
      setUser(data);
      resetAuthClearedFlag();
      identifyUser(data.id, { tier: data.tier });
      trackEvent("user_signed_up");

      // ─── Auto-apply system-level reduced-motion preference for new accounts ───
      // If the user's OS has "Reduce motion" enabled (vestibular disorder,
      // ADHD sensitivity, migraine), respect it by default. We only do this
      // on register (not on login) so existing users' explicit choices stick.
      try {
        const prefersReduced = typeof window !== "undefined"
          && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
        if (prefersReduced) {
          await axios.patch(`${API}/api/settings/appearance`,
            { reduced_motion: "reduced" },
            { withCredentials: true }
          ).catch(() => {}); // swallow — not critical to signup flow
        }
      } catch { /* ignore */ }

      return { success: true };
    } catch (e) {
      const body = e.response?.data || {};
      // Bundle E-4: a flagged address (typo suggestion / unknown mail server) is confirmable, not an error.
      if (e.response?.status === 422 && body.confirmable) {
        return { success: false, confirmable: true, suggestion: body.suggestion || null, reason: body.reason, error: formatApiErrorDetail(body.detail) };
      }
      return { success: false, error: formatApiErrorDetail(body.detail) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch (e) { logger.error("logout:", e); }
    trackEvent("user_logged_out");
    resetAnalytics();
    resetAuthClearedFlag();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, verifyTwoFactor, finishLogin, refreshUser, isAuthenticated: !!user, bootUnreachable }),
    [user, loading, login, register, logout, verifyTwoFactor, finishLogin, refreshUser, bootUnreachable]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
